import React, { useRef, useState } from 'react';
import Tool, { type ToolButtonComponent } from './Tool';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import { ApplicationView, useController } from '@aha-app/mvc';
import ToolButton from '../toolbars/ToolButton';
import WhiteboardController from '../controllers/WhiteboardController';
import { type Instance as TippyInstance } from 'tippy.js/index';
import { camelToSentenceCase } from '../utils/string';
import ToolButtonWithLabel from '../toolbars/ToolButtonWithLabel';

const SIZE_SCALE_VALUES = {
  small: 0.75,
  medium: 1,
  large: 1.25,
} as const;

const EM_DASH = '\u2014';

interface PopupProps {
  setCurrentSize: (size: string) => void;
  selection: any;
  hidePopup: () => void;
}

const Popup: React.FC<PopupProps> = ApplicationView(
  ({ setCurrentSize, selection, hidePopup }) => {
    const controller = useController(WhiteboardController);
    const sizes = Object.keys(SIZE_SCALE_VALUES);

    const setSize = size => {
      setCurrentSize(size);
      const scale = SIZE_SCALE_VALUES[size];
      selection.setOnObjects({ scaleX: scale, scaleY: scale });
      selection.setSize?.(size);
      hidePopup();
      Tool.toolActionComplete({ controller });
    };

    return (
      <div>
        <div style={{ maxHeight: 200, overflow: 'auto' }}>
          <ToolbarMenu columns={sizes.length}>
            {sizes.map(size => (
              <ToolButton
                key={size}
                onClick={() => setSize(size)}
                name={camelToSentenceCase(size)}
                // Using the first object of the selection as a proxy for all
                // of them is not ideal, but in practice it works well.
                active={
                  selection.firstObject().scaleX === SIZE_SCALE_VALUES[size]
                }
              >
                <i className={`far fa-${size.charAt(0)}`} />
              </ToolButton>
            ))}
          </ToolbarMenu>
        </div>
      </div>
    );
  }
);

const SizeButton: ToolButtonComponent = ApplicationView(({ selection }) => {
  const tippyRef = useRef<TippyInstance>();
  const size =
    Object.keys(SIZE_SCALE_VALUES).find(
      key => SIZE_SCALE_VALUES[key] === selection.firstObject().scaleX
    ) ?? 'custom';
  const [currentSize, setCurrentSize] = useState(size);
  const placeholder =
    currentSize === 'custom' ? (
      EM_DASH
    ) : (
      <i className={`far fa-${currentSize.charAt(0)}`} />
    );

  return (
    <ToolButtonWithLabel
      popup={
        <Popup
          setCurrentSize={setCurrentSize}
          selection={selection}
          hidePopup={() => tippyRef.current?.hide()}
        />
      }
      tippyRef={tippyRef}
      label={'Size'}
    >
      {placeholder}
    </ToolButtonWithLabel>
  );
});

class ToolSize extends Tool {
  static toolName = 'Size';

  static buttonComponent() {
    return SizeButton;
  }
}

export default ToolSize;
